import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Stack,
  TextField,
  MenuItem,
  Paper,
  Skeleton,
  TablePagination,
  Grid,
  Collapse,
} from "@mui/material";
import {
  TableColumnSelector,
  ColumnDefinition,
} from "../components/TableColumnSelector";
import { useSortableTable } from "../hooks/useSortableTable";
import { usePersistedColumns } from "../hooks/usePersistedColumns";
import {
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { useAuth } from "../state/AuthContext";
import { useTranslation } from "react-i18next";
import { api } from "../lib/apiClient";
import { AuditLogEntry } from "../types";
import { ExportMenu } from "../components/ExportMenu";
import CustomButton from "../components/CustomButton";
import {
  FilterToggleProvider,
  FilterButton,
  FilterCollapse,
} from "../components/FilterToggle";

export default function AuditLogsPage() {
  const {} = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  const columns: ColumnDefinition<AuditLogEntry>[] = [
    { id: "created_at", label: t("audit.table.date"), sortable: false },
    { id: "status", label: t("audit.table.status"), sortable: false },
    { id: "actor_username", label: t("audit.table.actor"), sortable: false },
    { id: "action", label: t("audit.table.action"), sortable: false },
    { id: "summary", label: t("audit.table.summary", "Resumo"), sortable: false },
    { id: "entity_type", label: t("audit.table.entity"), sortable: false },
    { id: "entity_id", label: t("audit.table.entityId"), sortable: false },
    { id: "details", label: t("audit.table.details"), sortable: false },
  ];

  const [visibleColumns, setVisibleColumns] = usePersistedColumns(
    "audit-logs-columns",
    [
      "created_at",
      "status",
      "actor_username",
      "action",
      "summary",
      "entity_type",
      "entity_id",
      "details",
    ]
  );

  const { orderBy, order, handleRequestSort, sortedData } =
    useSortableTable<AuditLogEntry>(logs);

  // Filters
  const [entityType, setEntityType] = useState("");
  const [actorId, setActorId] = useState("");
  const [entityId, setEntityId] = useState("");
  const [status, setStatus] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        limit: String(limit),
        offset: String(page * limit),
      };
      if (entityType) params.entityType = entityType;
      if (entityId) params.entityId = entityId;
      if (actorId) params.actorId = actorId;
      if (status) params.status = status;

      const data = await api.listAuditLogs(null, params);
      setLogs(data || []);
    } catch (error) {
      console.error("Failed to fetch audit logs", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [null, page, limit, entityType, entityId, actorId, status]);

  const formatValue = (val: any) => {
    if (val === null || val === undefined) return "---";
    if (typeof val === "boolean")
      return val ? t("common.yes", "Yes") : t("common.no", "No");
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  const renderChanges = (changes: any) => {
    if (!changes)
      return (
        <Typography variant="caption" color="text.secondary">
          {t("audit.details.noChanges")}
        </Typography>
      );

    let parsed = changes;
    if (typeof changes === "string") {
      try {
        parsed = JSON.parse(changes);
      } catch {
        return (
          <Typography variant="body2" color="text.primary">
            {changes}
          </Typography>
        );
      }
    }

    const translateField = (field: string) => {
      return t(`audit.details.fields.${field}`, { defaultValue: field });
    };

    try {
      // If it's the standard { field: { from, to } } or { field: value } format
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        const entries = Object.entries(parsed);
        if (entries.length === 0) {
          return (
            <Typography variant="caption" color="text.secondary">
              {t("audit.details.noChanges")}
            </Typography>
          );
        }

        // Check if any entry has the { from, to } structure
        const hasFromToFormat = entries.some(
          ([, delta]: [string, any]) =>
            delta &&
            typeof delta === "object" &&
            ("from" in delta || "to" in delta)
        );

        return (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Table size="small">
              <TableHead
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.primary.main, 0.1)
                      : theme.palette.grey[100],
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                    {t("audit.details.field")}
                  </TableCell>
                  {hasFromToFormat ? (
                    <>
                      <TableCell
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        {t("audit.details.old")}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        {t("audit.details.new")}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                      {t("audit.details.value", "Valor")}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(([field, delta]: [string, any]) => {
                  // Check if this is { from, to } format
                  const isFromToFormat =
                    delta &&
                    typeof delta === "object" &&
                    ("from" in delta || "to" in delta);

                  return (
                    <TableRow key={field}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          textTransform: "capitalize",
                        }}
                      >
                        {translateField(field)}
                      </TableCell>
                      {hasFromToFormat ? (
                        isFromToFormat ? (
                          <>
                            <TableCell
                              sx={{
                                color: "error.main",
                                textDecoration: delta.from
                                  ? "line-through"
                                  : "none",
                                opacity: 0.8,
                              }}
                            >
                              {formatValue(delta.from)}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 600, color: "success.main" }}
                            >
                              {formatValue(delta.to)}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ color: "text.secondary" }}>
                              ---
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 600, color: "success.main" }}
                            >
                              {formatValue(delta)}
                            </TableCell>
                          </>
                        )
                      ) : (
                        <TableCell
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formatValue(delta)}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        );
      }

      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.black, 0.3)
                : theme.palette.grey[100],
            borderRadius: 2,
            overflow: "auto",
            maxHeight: 300,
            fontSize: "0.85rem",
            border: "1px solid",
            borderColor: "divider",
            color: "text.primary",
            fontFamily: "monospace",
          }}
        >
          {JSON.stringify(parsed, null, 2)}
        </Box>
      );
    } catch {
      return (
        <Typography variant="body2" color="text.primary">
          {typeof changes === "string" ? changes : JSON.stringify(changes)}
        </Typography>
      );
    }
  };

  const renderMetadata = (metadata: any) => {
    if (!metadata) return null;

    let parsed = metadata;
    if (typeof metadata === "string") {
      try {
        parsed = JSON.parse(metadata);
      } catch {
        return null; // Or render raw string
      }
    }

    if (typeof parsed !== "object" || parsed === null) return null;

    const translateField = (field: string) => {
      return t(`audit.details.fields.${field}`, { defaultValue: field });
    };

    try {
      return (
        <Box
          sx={{
            p: 2,
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
            borderRadius: 2,
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
          }}
        >
          <Stack spacing={1}>
            {Object.entries(parsed).map(([key, value]) => (
              <Stack key={key} direction="row" spacing={1}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="info.main"
                  sx={{ minWidth: 80 }}
                >
                  {translateField(key)}:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: "monospace", color: "text.primary" }}
                >
                  {formatValue(value)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      );
    } catch {
      return null;
    }
  };

  const renderErrorDetails = (details: any) => {
    if (!details) return null;

    let parsed = details;
    if (typeof details === "string") {
      try {
        parsed = JSON.parse(details);
      } catch {
        return <Typography color="error">{details}</Typography>;
      }
    }

    if (!parsed || typeof parsed !== "object") {
      return <Typography color="error">{String(parsed)}</Typography>;
    }

    try {
      return (
        <Box
          sx={{
            p: 2,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
            borderRadius: 2,
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="error.main" fontWeight={700}>
              {String(parsed.message || "Error occurred")}
            </Typography>
            {parsed.code && (
              <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                Code: {String(parsed.code)}
              </Typography>
            )}
            {parsed.reason && (
              <Typography variant="body2">{String(parsed.reason)}</Typography>
            )}
            {parsed.stack && (
              <Box
                component="pre"
                sx={{
                  overflowX: "auto",
                  fontSize: "0.75rem",
                  p: 1,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                }}
              >
                {String(parsed.stack)}
              </Box>
            )}
          </Stack>
        </Box>
      );
    } catch (e) {
      return (
        <Typography color="error">
          {typeof details === "string" ? details : JSON.stringify(details)}
        </Typography>
      );
    }
  };

  return (
    <Stack spacing={3}>
      <FilterToggleProvider>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {t("audit.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("audit.subtitle")}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterButton />
            <ExportMenu
              data={sortedData}
              filename="audit-logs-export"
              columns={[
                {
                  id: "created_at",
                  label: t("audit.table.date"),
                  transform: (val) =>
                    val ? new Date(val).toLocaleString() : "—",
                },
                { id: "status", label: t("audit.table.status") },
                { id: "actor_username", label: t("audit.table.actor") },
                { id: "action", label: t("audit.table.action") },
                { id: "entity_type", label: t("audit.table.entity") },
                { id: "entity_id", label: t("audit.table.entityId") },
                {
                  id: "changes",
                  label: t("audit.table.details"),
                  transform: (val, row) => {
                    const details = val || row.metadata || row.error_details;
                    return typeof details === "object"
                      ? JSON.stringify(details)
                      : String(details || "");
                  },
                },
              ]}
            />
            <Tooltip title={t("common.refresh")}>
              <span>
                <CustomButton
                  variant="icon"
                  icon={<RefreshIcon />}
                  iconPosition="only"
                  customSize={{ width: 36, height: 36 }}
                  onClick={fetchLogs}
                  disabled={loading}
                />
              </span>
            </Tooltip>
          </Stack>
        </Stack>

      <FilterCollapse>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            label={t("audit.filters.entityType")}
            select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            size="small"
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t("audit.filters.all")}</MenuItem>
            <MenuItem value="USER">{t("audit.entities.USER")}</MenuItem>
            <MenuItem value="TENANT">{t("audit.entities.TENANT")}</MenuItem>
            <MenuItem value="DEALER">{t("audit.entities.DEALER")}</MenuItem>
            <MenuItem value="APPLICATION">
              {t("audit.entities.APPLICATION")}
            </MenuItem>
            <MenuItem value="MODULE">{t("audit.entities.MODULE")}</MenuItem>
          </TextField>
          <TextField
            label={t("audit.filters.entityId")}
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            size="small"
            sx={{ minWidth: 160 }}
            placeholder="UUID..."
          />
          <TextField
            label={t("audit.filters.status", "Status")}
            select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">{t("audit.filters.all", "All")}</MenuItem>
            <MenuItem value="SUCCESS">
              {t("common.success", "Success")}
            </MenuItem>
            <MenuItem value="FAILURE">
              {t("common.failure", "Failure")}
            </MenuItem>
            <MenuItem value="ERROR">{t("common.error", "Error")}</MenuItem>
          </TextField>
        </Stack>
      </FilterCollapse>
      </FilterToggleProvider>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: (theme) =>
            `0 4px 20px 0 ${alpha(theme.palette.common.black, 0.05)}`,
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ p: 2, pb: 0 }}>
          <TableColumnSelector
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={setVisibleColumns}
          />
        </Stack>
        <Table size="medium">
          <TableHead
            sx={{
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.1)
                  : theme.palette.grey[100],
            }}
          >
            <TableRow>
              {visibleColumns.includes("created_at") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.date")}
                </TableCell>
              )}
              {visibleColumns.includes("status") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.status")}
                </TableCell>
              )}
              {visibleColumns.includes("actor_username") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.actor")}
                </TableCell>
              )}
              {visibleColumns.includes("action") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.action")}
                </TableCell>
              )}
              {visibleColumns.includes("summary") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.summary", "Resumo")}
                </TableCell>
              )}
              {visibleColumns.includes("entity_type") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.entity")}
                </TableCell>
              )}
              {visibleColumns.includes("entity_id") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.entityId")}
                </TableCell>
              )}
              {visibleColumns.includes("details") && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("audit.table.details")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={visibleColumns.length}>
                    <Skeleton animation="wave" height={24} />
                  </TableCell>
                </TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Typography color="text.secondary">
                    {t("audit.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow
                  key={log.id}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {visibleColumns.includes("created_at") && (
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(log.created_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes("status") && (
                    <TableCell>
                      {!log.status || log.status === "SUCCESS" ? (
                        <Tooltip title={t("common.success")}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </Tooltip>
                      ) : log.status === "FAILURE" ? (
                        <Tooltip title={t("common.failure")}>
                          <WarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t("common.error")}>
                          <ErrorIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes("actor_username") && (
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {log.actor_username || t("audit.details.system")}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.action.focus, 0.1)
                                : theme.palette.grey[100],
                            px: 0.8,
                            py: 0.2,
                            borderRadius: 1,
                            color: "text.primary",
                            fontWeight: 500,
                          }}
                        >
                          {t(`roles.${log.actor_role}`, {
                            defaultValue: log.actor_role,
                          })}
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes("action") && (
                    <TableCell>
                      <Chip
                        label={log.action}
                        size="small"
                        sx={(theme) => {
                          const isDark = theme.palette.mode === "dark";
                          let bgColor, textColor, borderColor;
                          if (log.action === "DELETE") {
                            bgColor = isDark
                              ? alpha("#dc2626", 0.2)
                              : alpha("#dc2626", 0.2);
                            textColor = isDark ? "#fca5a5" : "#7f1d1d";
                            borderColor = isDark
                              ? alpha("#dc2626", 0.4)
                              : alpha("#dc2626", 0.4);
                          } else if (log.action === "CREATE") {
                            bgColor = isDark
                              ? alpha("#16a34a", 0.2)
                              : alpha("#16a34a", 0.2);
                            textColor = isDark ? "#86efac" : "#14532d";
                            borderColor = isDark
                              ? alpha("#16a34a", 0.4)
                              : alpha("#16a34a", 0.4);
                          } else {
                            // UPDATE, etc. - use primary blue
                            bgColor = isDark
                              ? alpha(theme.palette.primary.main, 0.2)
                              : alpha(theme.palette.primary.main, 0.2);
                            textColor = isDark
                              ? theme.palette.primary.light
                              : theme.palette.primary.dark;
                            borderColor = isDark
                              ? alpha(theme.palette.primary.main, 0.4)
                              : alpha(theme.palette.primary.main, 0.4);
                          }
                          return {
                            fontWeight: 700,
                            borderRadius: 1.5,
                            backgroundColor: bgColor,
                            color: textColor,
                            border: `1px solid ${borderColor}`,
                          };
                        }}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.includes("summary") && (
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                        {log.summary || `${t(`audit.actions.${log.action}`, { defaultValue: log.action })} ${t(`audit.entities.${log.entity_type}`, { defaultValue: log.entity_type })}`}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes("entity_type") && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {t(`audit.entities.${log.entity_type}`, {
                          defaultValue: log.entity_type,
                        })}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes("entity_id") && (
                    <TableCell>
                      <Tooltip title={log.entity_id || ""} arrow>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "action.hover"
                                : theme.palette.grey[100],
                            p: 0.5,
                            borderRadius: 1,
                            color: "text.primary",
                          }}
                        >
                          {log.entity_id
                            ? `${log.entity_id.substring(0, 8)}...`
                            : "---"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  )}
                  {visibleColumns.includes("details") && (
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                        <Box sx={{ flexGrow: 1, minWidth: 50, maxWidth: 300 }}>
                          {(() => {
                            let reason = "";
                            if (log.status === 'FAILURE' || log.status === 'ERROR') {
                               const details = log.error_details || log.metadata || log.changes;
                               if (typeof details === 'string') {
                                 try {
                                   const p = JSON.parse(details);
                                   reason = p.message || p.reason || p.error || details;
                                 } catch(e) {
                                   reason = details;
                                 }
                               } else if (typeof details === 'object' && details) {
                                 reason = (details as any).message || (details as any).reason || (details as any).error || "";
                               }
                            } else if (log.action === 'REVOKE' || log.action === 'UNREVOKE') {
                               const details = log.metadata || log.changes;
                               if (typeof details === 'string') {
                                 try {
                                   const p = JSON.parse(details);
                                   reason = p.reason || p.message || "";
                                 } catch(e) {}
                               } else if (typeof details === 'object' && details) {
                                 reason = (details as any).reason || (details as any).message || "";
                               }
                            }
                            
                            if (reason) {
                               return (
                                 <Chip 
                                   label={String(reason)} 
                                   size="small" 
                                   color={log.status === 'FAILURE' || log.status === 'ERROR' ? "error" : "default"}
                                   variant={log.status === 'FAILURE' || log.status === 'ERROR' ? "filled" : "outlined"}
                                   sx={{ maxWidth: '100%', "& .MuiChip-label": { display: "block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" } }} 
                                 />
                               );
                            }
                            return <Typography variant="caption" color="text.secondary">---</Typography>;
                          })()}
                        </Box>
                        <Tooltip title={t("common.view")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setSelectedLog(log)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={-1}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage={t("common.rowsPerPage", "Rows per page")}
          nextIconButtonProps={{ disabled: logs.length < limit }}
        />
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {t("audit.details.title")}
          </Typography>
          <Tooltip title={t("common.close")}>
            <span>
              <CustomButton
                variant="icon"
                icon={<CloseIcon />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={() => setSelectedLog(null)}
              />
            </span>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedLog && (
            <Stack spacing={4}>
              {selectedLog.summary && (
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedLog.summary}
                  </Typography>
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t("audit.table.date")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {new Date(selectedLog.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t("audit.table.actor")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedLog.actor_username || "System"}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t("audit.table.action")}
                  </Typography>
                  <Chip
                    label={selectedLog.action}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 700 }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {t("audit.table.entity")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedLog.entity_type}
                  </Typography>
                </Grid>
              </Grid>

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {t("audit.details.changes")}
                  <Box
                    sx={{
                      flexGrow: 1,
                      height: "1px",
                      bgcolor: (t) =>
                        alpha(t.palette.primary.main, 0.1),
                    }}
                  />
                </Typography>
                {renderChanges(selectedLog.changes)}
              </Box>

              {selectedLog.metadata && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="info.main"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {t("audit.details.metadata")}
                    <Box
                      sx={{
                        flexGrow: 1,
                        height: "1px",
                        bgcolor: (t) =>
                          alpha(t.palette.info.main, 0.1),
                      }}
                    />
                  </Typography>
                  {renderMetadata(selectedLog.metadata)}
                </Box>
              )}

              {selectedLog.error_details && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="error.main"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {t("audit.details.error")}
                    <Box
                      sx={{
                        flexGrow: 1,
                        height: "1px",
                        bgcolor: (t) =>
                          alpha(t.palette.error.main, 0.1),
                      }}
                    />
                  </Typography>
                  {renderErrorDetails(selectedLog.error_details)}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.action.hover, 0.02),
          }}
        >
          <CustomButton
            variant="primary"
            onClick={() => setSelectedLog(null)}
          >
            {t("common.close")}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
